import { useMemo } from "react";

import { faArrowUpRight, faSearch } from "@fortawesome/pro-regular-svg-icons";
import { createColumnHelper } from "@tanstack/react-table";

import { Cell, Header, TableComp } from "src/base-components/Table";
import { TruncatedIdPill } from "src/base-components/TruncatedIdPill";
import { CaseBlock, CaseBlockProps } from "src/cases/caseView/CaseBlock";
import { CaseStatusPill as CaseStatusPillComponent } from "src/cases/common/CaseStatusPill";
import { useCases } from "src/cases/queries/cases";
import { CasesDeserialized } from "src/cases/queries/casesTypes";
import { useCaseTemplatesWithVersions } from "src/cases/queries/templates";
import { CaseTemplatePill } from "src/cases/queues/CasesTableValues";
import { getPropertyDisplay, getSubValueDisplayName } from "src/cases/utils";
import {
  CaseEnvironment,
  GetCasesApiV1CasesGetFilterEnvEnum,
  RelatedCasesCaseBlockConfigAttributes,
} from "src/clients/case-management";
import { EmptyState } from "src/design-system/EmptyState";
import { Icon } from "src/design-system/Icon";
import { CasePageParams, getUrlToCaseViewPage } from "src/router/urls";
import { useParamsDecode } from "src/utils/useParamsDecode";

type DeserializedCase = CasesDeserialized["data"][number];

const helper = createColumnHelper<DeserializedCase>();

type RelatedCasesBlockProps = Omit<CaseBlockProps, "children"> & {
  data: RelatedCasesCaseBlockConfigAttributes;
  caseEnv: CaseEnvironment;
  propertyKey: string;
};

const useRelatedCases = ({
  entityType,
  entityId,
  propertyKey,
  caseEnv,
}: {
  entityType: string;
  entityId?: string | null;
  propertyKey: string;
  caseEnv: CaseEnvironment;
}) => {
  // Convert CaseEnvironment to the API enum type
  const environment: GetCasesApiV1CasesGetFilterEnvEnum =
    caseEnv === "sandbox"
      ? GetCasesApiV1CasesGetFilterEnvEnum.SANDBOX
      : GetCasesApiV1CasesGetFilterEnvEnum.LIVE;

  // Construct filters for the API call
  const filters = useMemo(() => {
    if (!entityType || !entityId) return undefined;

    return {
      "filter[column_entity_type]": entityType,
      [`filter[properties.${propertyKey}]`]: entityId,
    };
  }, [entityType, entityId, propertyKey]);

  return useCases({
    environment,
    filters,
  });
};

// This hook is made specifically to be used in this block
// it fetches all possible templates and versions to make sure we can display
// every possible case data, if unsure do no use it in other places
const useCaseTemplateData = ({
  templateId,
  versionId,
}: {
  templateId: string;
  versionId: string;
}) => {
  const { data: templates = [] } = useCaseTemplatesWithVersions();

  const template = templates.find((t) => t.id === templateId);
  const version = versionId
    ? template?.versions?.find((v) => v.id === versionId)
    : template?.versions?.[0];

  return { template, version };
};

const buildCaseColumns = () => {
  const columns = [
    helper.accessor("id", {
      header: (info) => <Header info={info}>Case ID</Header>,
      size: 100,
      cell: (info) => (
        <Cell className="w-24" info={info}>
          <TruncatedIdPill
            feedback="inline-only-checkmark"
            id={info.getValue()}
          />
        </Cell>
      ),
    }),
    helper.accessor("case_properties.status", {
      header: (info) => <Header info={info}>Status</Header>,
      size: 150,
      cell: (info) => {
        const caseData = info.row.original;

        return (
          <Cell info={info}>
            <CaseStatusPill caseData={caseData} />
          </Cell>
        );
      },
    }),
    helper.accessor("template.id", {
      header: (info) => <Header info={info}>Template</Header>,
      size: 200,
      cell: (info) => {
        const caseData = info.row.original;

        return (
          <Cell info={info}>
            <CaseTemplatePill
              templateId={caseData.template.id}
              versionId={caseData.template_version.id}
            />
          </Cell>
        );
      },
    }),
    helper.display({
      id: "actions",
      size: 48,
      minSize: 48,
      maxSize: 48,
      meta: {
        sticky: "right",
      },
      cell: (info) => (
        <Cell
          className="items-center justify-end group-hover:bg-gray-50"
          info={info}
        >
          <div className="cursor-pointer opacity-0 transition-opacity group-hover:opacity-100">
            <Icon icon={faArrowUpRight} size="sm" />
          </div>
        </Cell>
      ),
    }),
  ];

  return columns;
};

export const RelatedCasesBlock: React.FC<RelatedCasesBlockProps> = ({
  data,
  caseEnv,
  propertyKey,
  ...props
}) => {
  const { orgId, wsId, case_id: caseId } = useParamsDecode<CasePageParams>();

  const { data: relatedCasesData, isLoading } = useRelatedCases({
    entityType: data.entity.type,
    entityId: data.entity.id,
    propertyKey,
    caseEnv,
  });

  // Display only cases that are not the current case
  const relatedCases = relatedCasesData?.filter((c) => c.id !== caseId);

  const columns = useMemo(() => {
    return buildCaseColumns();
  }, []);

  if (!data.entity.id) {
    return (
      <CaseBlock {...props}>
        <EmptyState
          dataLoc="related-cases-no-entity"
          description="This block currently has no linked entity."
          headline="No entity provided"
          icon={faSearch}
          size="compact"
        />
      </CaseBlock>
    );
  }

  const hasNoCases = !isLoading && relatedCases?.length === 0;

  if (hasNoCases) {
    return (
      <CaseBlock {...props}>
        <EmptyState
          dataLoc="related-cases-empty-state"
          description="No related cases found for this entity."
          headline="No related cases"
          icon={faSearch}
          size="compact"
        />
      </CaseBlock>
    );
  }

  return (
    <CaseBlock
      {...props}
      isLoading={isLoading}
      noPaddings={true}
      noScroll={true}
    >
      <div className="flex h-full flex-col p-4">
        <div className="min-h-0 flex-1">
          <TableComp
            columns={columns}
            data={relatedCases || []}
            dataLoc="related-cases-table"
            frameClassName="h-full"
            getRowId={(row) => row.id}
            isLoading={isLoading}
            rowClassName="group hover:bg-gray-50 cursor-pointer"
            rowPropsGetter={(row) => ({
              onClick: () => {
                const caseId = row.original.id;
                const caseUrl = getUrlToCaseViewPage(orgId, wsId, caseId);
                window.open(`/decide${caseUrl}`, "_blank");
              },
            })}
            variant="compact"
          />
        </div>
      </div>
    </CaseBlock>
  );
};

const CaseStatusPill = ({ caseData }: { caseData: DeserializedCase }) => {
  const { status, sub_status: subStatus } = caseData.case_properties;
  const { version } = useCaseTemplateData({
    templateId: caseData.template.id,
    versionId: caseData.template_version.id,
  });

  const statusSchema = version?.attributes.case_properties.status;
  const { variant, displayName: statusDisplayName } = getPropertyDisplay(
    status,
    statusSchema,
  );
  const subStatusDisplayName = getSubValueDisplayName(
    status,
    subStatus,
    statusSchema,
  );

  return (
    <CaseStatusPillComponent
      status={statusDisplayName}
      subStatus={subStatusDisplayName || subStatus}
      variant={variant}
    />
  );
};
